import React, { ReactElement, ReactNode, useCallback, useEffect, useRef } from 'react'
import { ListChildComponentProps } from 'react-window'

import ErrorBoundary from '@components/ErrorBoundary'
import bem from '@lib/bem'

export interface ItemData<T = any> {
  setRowHeight: (index: number, height: number) => void
  items: T[]
  renderElement: (item: T) => ReactNode
}

type ItemProps = ListChildComponentProps<ItemData>

const ITEM_VERTICAL_GAP = 25

const Item = ({ style, index, data }: ItemProps): ReactElement => {
  const { setRowHeight, items, renderElement } = data
  const rowRef = useRef<HTMLDivElement | null>(null)

  const resizeObserverRef = useRef<ResizeObserver | null>(null)
  const lastMeasuredTotalHeight = useRef<number>(0)

  const updateContentHeight = useCallback(() => {
    const contentElement = rowRef.current?.children[0] as HTMLElement | undefined
    if (!contentElement) return
    // Get the actual height of the content element itself (the rendered card).
    // ResizeObserver fires AFTER layout changes, so this height should be accurate
    const contentHeight = contentElement.getBoundingClientRect().height

    // The total height reported to react-window MUST include the desired gap.
    const totalItemHeight = contentHeight + ITEM_VERTICAL_GAP

    // Only update if there's a significant change or it's the first measurement.
    if (Math.abs(totalItemHeight - lastMeasuredTotalHeight.current) > 1 || lastMeasuredTotalHeight.current === 0) {
      lastMeasuredTotalHeight.current = totalItemHeight
      setRowHeight(index, totalItemHeight) // Send the calculated total height
    }
  }, [setRowHeight, index])

  useEffect(() => {
    const contentElement = rowRef.current?.children[0]
    if (contentElement) {
      resizeObserverRef.current = new ResizeObserver(entries => {
        for (const entry of entries) {
          if (entry.target === contentElement) {
            updateContentHeight()
            break
          }
        }
      })
      resizeObserverRef.current.observe(contentElement)
      requestAnimationFrame(() => {
        updateContentHeight()
      })
      return () => {
        if (resizeObserverRef.current) {
          resizeObserverRef.current.disconnect()
          resizeObserverRef.current = null
        }
      }
    }
  }, [updateContentHeight])

  return (
    <div className={bem('virtual-list', 'item')} style={style} ref={rowRef}>
      <ErrorBoundary fallback={() => <></>}>{renderElement(items[index])}</ErrorBoundary>
    </div>
  )
}

export default Item
